import { useMemo } from 'react';

import { InvestorDetailsLegalEntityAssetClassTargetsQuery } from '@/__generated__/graphql';
import FormattingTooltip from '@/components/basicComponents/tooltip/dollarsTooltip';
import { InvestorDetailsEntityTypeEnum } from '@/components/investors-page/investorDetailsPage/types';
import { MobileRow, MobileRowTitle, MobileRowValue, MobileTableRowWrap } from '@/components/table/mobileTable/mobileTable';
import { themeVar } from '@/styles/theme';
import styled from 'styled-components';
import {
  overCommitmentRatioModeSummaryTableColumns,
  pacingModelModeSimplifiedSummaryTableColumns,
  pacingModelModeSummaryTableColumns
} from '../../../constants';
import { calculateCapacity } from '../tableSumFooter';

interface SummaryMobileTableSumFooterProps {
  investorSummaryRows: InvestorDetailsLegalEntityAssetClassTargetsQuery['legalEntity']['assetClassTargets'];
  ignoreAllocationTargets: boolean;
  commitmentPlansTotal: number | null;
  targetOCRatio: number | null;
  targetSubAssetClasses: boolean;
  entityType: InvestorDetailsEntityTypeEnum;
}

interface ISum {
  total: string;
  target: number;
  targetAllocations: number;
  exposure: number;
  capacity: number;
  currentNAV: number;
  unfundedCommitments: number;
}

const SummaryMobileTableSumFooter = ({
  investorSummaryRows,
  ignoreAllocationTargets,
  commitmentPlansTotal,
  targetOCRatio,
  targetSubAssetClasses,
  entityType
}: SummaryMobileTableSumFooterProps) => {
  const rowSum: ISum = useMemo(() => {
    const sum = {
      total: 'Total',
      target: 0,
      targetAllocations: 0,
      exposure: 0,
      capacity: 0,
      currentNAV: 0,
      unfundedCommitments: 0
    };

    investorSummaryRows.forEach((row) => {
      sum.target += Number((row.percentage || 0).toFixed(2));
      sum.targetAllocations += row.targetAllocation;
      sum.exposure += row.economicExposure;
      sum.currentNAV += row.currentNAV || 0;
      sum.unfundedCommitments += row.unfundedCommitments || 0;
      sum.capacity += calculateCapacity(row, commitmentPlansTotal, targetOCRatio, targetSubAssetClasses);
    });

    return sum;
  }, [investorSummaryRows, commitmentPlansTotal, targetOCRatio]);

  // if (ignoreAllocationTargets && targetOCRatio !== null) {
  //   return (
  //     <>
  //       {investorSummaryRows && (
  //         <MobileSumFooterWrap>
  //           <MobileRow>
  //             <MobileRowTitle>{overCommitmentRatioModeSimplifiedSummaryTableColumns[0].title}</MobileRowTitle>
  //             <MobileRowValue>{rowSum.total}</MobileRowValue>
  //           </MobileRow>

  //           <MobileRow>
  //             <MobileRowTitle>{overCommitmentRatioModeSimplifiedSummaryTableColumns[1].title}</MobileRowTitle>
  //             <MobileRowValue>
  //               <FormattingTooltip>{rowSum.exposure}</FormattingTooltip>
  //             </MobileRowValue>
  //           </MobileRow>
  //         </MobileSumFooterWrap>
  //       )}
  //     </>
  //   );
  // }

  if (ignoreAllocationTargets || entityType === InvestorDetailsEntityTypeEnum.GroupedEntity) {
    return (
      <>
        {investorSummaryRows && (
          <MobileSumFooterWrap>
            <MobileRow>
              <MobileRowTitle>{pacingModelModeSimplifiedSummaryTableColumns[0].title}</MobileRowTitle>
              <MobileRowValue>{rowSum.total}</MobileRowValue>
            </MobileRow>

            <MobileRow>
              <MobileRowTitle>{pacingModelModeSimplifiedSummaryTableColumns[1].title}</MobileRowTitle>
              <MobileRowValue>
                <FormattingTooltip>{rowSum.currentNAV}</FormattingTooltip>
              </MobileRowValue>
            </MobileRow>

            <MobileRow>
              <MobileRowTitle>{pacingModelModeSimplifiedSummaryTableColumns[2].title}</MobileRowTitle>
              <MobileRowValue>
                <FormattingTooltip>{rowSum.unfundedCommitments}</FormattingTooltip>
              </MobileRowValue>
            </MobileRow>
          </MobileSumFooterWrap>
        )}
      </>
    );
  }

  if (targetOCRatio === null) {
    return (
      <>
        {investorSummaryRows && (
          <MobileSumFooterWrap>
            <MobileRow>
              <MobileRowTitle>{pacingModelModeSummaryTableColumns[0].title}</MobileRowTitle>
              <MobileRowValue>{rowSum.total}</MobileRowValue>
            </MobileRow>

            <MobileRow>
              <MobileRowTitle>{pacingModelModeSummaryTableColumns[1].title}</MobileRowTitle>
              <MobileRowValue>{rowSum.target.toFixed(2)}%</MobileRowValue>
            </MobileRow>

            <MobileRow>
              <MobileRowTitle>{pacingModelModeSummaryTableColumns[2].title}</MobileRowTitle>
              <MobileRowValue>
                <FormattingTooltip>{rowSum.targetAllocations}</FormattingTooltip>
              </MobileRowValue>
            </MobileRow>

            <MobileRow>
              <MobileRowTitle>{pacingModelModeSummaryTableColumns[3].title}</MobileRowTitle>
              <MobileRowValue>
                <FormattingTooltip>{rowSum.currentNAV}</FormattingTooltip>
              </MobileRowValue>
            </MobileRow>

            <MobileRow>
              <MobileRowTitle>{pacingModelModeSummaryTableColumns[4].title}</MobileRowTitle>
              <MobileRowValue>
                <FormattingTooltip>{rowSum.unfundedCommitments}</FormattingTooltip>
              </MobileRowValue>
            </MobileRow>

            <MobileRow>
              <MobileRowTitle>{pacingModelModeSummaryTableColumns[5].title}</MobileRowTitle>
              <MobileRowValue>{commitmentPlansTotal === 0 ? <>--</> : <FormattingTooltip>{rowSum.capacity}</FormattingTooltip>}</MobileRowValue>
            </MobileRow>
          </MobileSumFooterWrap>
        )}
      </>
    );
  }

  return (
    <>
      {investorSummaryRows && (
        <MobileSumFooterWrap>
          <MobileRow>
            <MobileRowTitle>{overCommitmentRatioModeSummaryTableColumns[0].title}</MobileRowTitle>
            <MobileRowValue>{rowSum.total}</MobileRowValue>
          </MobileRow>

          <MobileRow>
            <MobileRowTitle>{overCommitmentRatioModeSummaryTableColumns[1].title}</MobileRowTitle>
            <MobileRowValue>{rowSum.target.toFixed(2)}%</MobileRowValue>
          </MobileRow>

          <MobileRow>
            <MobileRowTitle>{overCommitmentRatioModeSummaryTableColumns[2].title}</MobileRowTitle>
            <MobileRowValue>
              <FormattingTooltip>{rowSum.targetAllocations}</FormattingTooltip>
            </MobileRowValue>
          </MobileRow>

          <MobileRow>
            <MobileRowTitle>{overCommitmentRatioModeSummaryTableColumns[3].title}</MobileRowTitle>
            <MobileRowValue>
              <FormattingTooltip>{rowSum.exposure}</FormattingTooltip>
            </MobileRowValue>
          </MobileRow>

          <MobileRow>
            <MobileRowTitle>{overCommitmentRatioModeSummaryTableColumns[4].title}</MobileRowTitle>
            <MobileRowValue>
              <FormattingTooltip>{rowSum.capacity}</FormattingTooltip>
            </MobileRowValue>
          </MobileRow>
        </MobileSumFooterWrap>
      )}
    </>
  );
};

export default SummaryMobileTableSumFooter;

const MobileSumFooterWrap = styled(MobileTableRowWrap)`
  background-color: ${themeVar('layer.2')};
`;
